import React from 'react'
import { useFormsStyles } from '../forms.css'
import { Form, FormSpy } from 'react-final-form'
import { SubmitButtonProps } from 'components/common/submitButton'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { ApplicationState } from 'core/store/reducers'
import { Grid, Typography } from '@material-ui/core'
import CheckboxGroupField from '../components/checkboxGroupField'

interface Props {
    subscription?: { [property: string]: boolean }
    onSubmit: (values: NotificationPreferenceFormState) => void
    children: (content: React.ReactNode, actions: Array<SubmitButtonProps>) => React.ReactNode
    inProgress: boolean
    formClassName?: string
    initialValues: NotificationPreferenceFormState
    hideDays: boolean
    hideNotification: boolean
}

export interface NotificationPreferenceFormState {
    allow_notification: boolean
    allow_messaging_notification: boolean
    mon: boolean
    tue: boolean
    wed: boolean
    thu: boolean
    fri: boolean
    sat: boolean
    sun: boolean
}

export interface NotificationPreferenceFormStateInternal {
    allow_booking_notification: boolean[]
    allow_notification: boolean[]
    allow_messaging_notification: boolean[]
    mon: boolean[]
    tue: boolean[]
    wed: boolean[]
    thu: boolean[]
    fri: boolean[]
    sat: boolean[]
    sun: boolean[]
}

const NotificationPreferenceForm: React.FC<Props> = ({
    subscription = {
        submitting: true,
        pristine: true,
    },
    onSubmit,
    children,
    inProgress,
    formClassName,
    initialValues,
    hideDays,
    hideNotification,
}) => {
    const formsClasses = useFormsStyles()
    const { t } = useTranslation()
    const { ability } = useSelector(({ app }: ApplicationState) => ({
        ability: app.ability,
    }))

    const initialValuesInternal: NotificationPreferenceFormStateInternal = {
        allow_booking_notification: [true],
        allow_notification: [initialValues.allow_notification],
        allow_messaging_notification: [initialValues.allow_messaging_notification],
        mon: [initialValues.mon],
        tue: [initialValues.tue],
        wed: [initialValues.wed],
        thu: [initialValues.thu],
        fri: [initialValues.fri],
        sat: [initialValues.sat],
        sun: [initialValues.sun],
    }

    const handleSubmit = (values: NotificationPreferenceFormStateInternal) => {
        return onSubmit({
            allow_notification: values.allow_notification[0] ?? false,
            allow_messaging_notification: values.allow_messaging_notification[0] ?? false,
            mon: values.mon[0] ?? false,
            tue: values.tue[0] ?? false,
            wed: values.wed[0] ?? false,
            thu: values.thu[0] ?? false,
            fri: values.fri[0] ?? false,
            sat: values.sat[0] ?? false,
            sun: values.sun[0] ?? false,
        })
    }

    return (
        <Form
            initialValues={initialValuesInternal}
            subscription={subscription}
            debug={console.log}
            mutators={{
                updateNotificationStatus: (args, state, tools) => {
                    const newValue = args[0] as boolean
                    if (newValue) {
                        tools.changeValue(state, 'mon', () => [true])
                        tools.changeValue(state, 'tue', () => [true])
                        tools.changeValue(state, 'wed', () => [true])
                        tools.changeValue(state, 'thu', () => [true])
                        tools.changeValue(state, 'fri', () => [true])
                        tools.changeValue(state, 'sat', () => [true])
                        tools.changeValue(state, 'sun', () => [true])
                    } else {
                        tools.changeValue(state, 'mon', () => [false])
                        tools.changeValue(state, 'tue', () => [false])
                        tools.changeValue(state, 'wed', () => [false])
                        tools.changeValue(state, 'thu', () => [false])
                        tools.changeValue(state, 'fri', () => [false])
                        tools.changeValue(state, 'sat', () => [false])
                        tools.changeValue(state, 'sun', () => [false])
                    }
                },
            }}
            onSubmit={handleSubmit}
            keepDirtyOnReinitialize
        >
            {({ handleSubmit, submitting, form: { mutators } }) => (
                <form onSubmit={handleSubmit} noValidate className={formClassName}>
                    {children(
                        <>
                            {!hideNotification && (
                                <>
                                    {ability.can('api.booking_access') && (
                                        <CheckboxGroupField
                                            name="allow_booking_notification"
                                            values={[
                                                {
                                                    label: t('form.allowBookingNotifications'),
                                                    value: true,
                                                },
                                            ]}
                                            xs={12}
                                            type="switch"
                                            disabled
                                        />
                                    )}
                                    {ability.can('api.conversation_access') && (
                                        <CheckboxGroupField
                                            name="allow_messaging_notification"
                                            values={[
                                                {
                                                    label: t('form.allowMessagingNotification'),
                                                    value: true,
                                                },
                                            ]}
                                            xs={12}
                                            type="switch"
                                        />
                                    )}
                                    <CheckboxGroupField
                                        name="allow_notification"
                                        values={[
                                            {
                                                label: t('form.allowNotifications'),
                                                value: true,
                                            },
                                        ]}
                                        xs={12}
                                        type="switch"
                                        onUpdate={(_, checked) => mutators.updateNotificationStatus(checked)}
                                    />
                                </>
                            )}
                            {!hideDays && (
                                <>
                                    <Typography>
                                        <Trans i18nKey="form.notificationDay" />
                                    </Typography>
                                    <FormSpy subscription={{ values: true }}>
                                        {(props) => (
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <CheckboxGroupField
                                                        name="mon"
                                                        values={[
                                                            {
                                                                label: t('form.monday'),
                                                                value: true,
                                                            },
                                                        ]}
                                                        xs={12}
                                                        type="switch"
                                                        disabled={!props.values.allow_notification}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <CheckboxGroupField
                                                        name="tue"
                                                        values={[
                                                            {
                                                                label: t('form.tuesday'),
                                                                value: true,
                                                            },
                                                        ]}
                                                        xs={12}
                                                        type="switch"
                                                        disabled={!props.values.allow_notification}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <CheckboxGroupField
                                                        name="wed"
                                                        values={[
                                                            {
                                                                label: t('form.wednesday'),
                                                                value: true,
                                                            },
                                                        ]}
                                                        xs={12}
                                                        type="switch"
                                                        disabled={!props.values.allow_notification}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <CheckboxGroupField
                                                        name="thu"
                                                        values={[
                                                            {
                                                                label: t('form.thursday'),
                                                                value: true,
                                                            },
                                                        ]}
                                                        xs={12}
                                                        type="switch"
                                                        disabled={!props.values.allow_notification}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <CheckboxGroupField
                                                        name="fri"
                                                        values={[
                                                            {
                                                                label: t('form.friday'),
                                                                value: true,
                                                            },
                                                        ]}
                                                        xs={12}
                                                        type="switch"
                                                        disabled={!props.values.allow_notification}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <CheckboxGroupField
                                                        name="sat"
                                                        values={[
                                                            {
                                                                label: t('form.saturday'),
                                                                value: true,
                                                            },
                                                        ]}
                                                        xs={12}
                                                        type="switch"
                                                        disabled={!props.values.allow_notification}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <CheckboxGroupField
                                                        name="sun"
                                                        values={[
                                                            {
                                                                label: t('form.sunday'),
                                                                value: true,
                                                            },
                                                        ]}
                                                        xs={12}
                                                        type="switch"
                                                        disabled={!props.values.allow_notification}
                                                    />
                                                </Grid>
                                            </Grid>
                                        )}
                                    </FormSpy>
                                </>
                            )}
                        </>,
                        [
                            {
                                type: 'submit',
                                loading: submitting || inProgress,
                                className: formsClasses.submit,
                                children: <Trans i18nKey="actions.continue" />,
                            },
                        ],
                    )}
                </form>
            )}
        </Form>
    )
}

export default NotificationPreferenceForm
